import { Box, Space } from '@looker/components';
import { BIIconButton } from '@pulsepointinc/bi-components';
import type { DsInputCustomEvent } from '@pulsepointinc/design-system';
import { DsInput } from '@pulsepointinc/design-system-react';
import { type FC, useMemo, useState } from 'react';
import { sanitizeFilterExpression } from '../../../../../../helpers';
import { useFilters } from '../../../../../../hooks/useFilters';
import type { ExpressionType, Filter } from '../../../../../../stores/filters';
import { toTitleCase } from '../TreeIncludeExcludeFilter/helpers';
import { StyledBISelect } from './style';

interface ParamFilterProps {
  filterName: string;
  paramOptions: string[];
  paramKey: string;
  onChange?: (filter: Filter) => void;
  onParamChange?: (param: string) => void;
  removeFilter?: () => void;
}

export const ParamFilter: FC<ParamFilterProps> = ({
  filterName,
  paramKey,
  paramOptions,
  onParamChange,
  onChange,
  removeFilter,
}) => {
  const [selectedParam, setSelectedParam] = useState<string>(paramKey || paramOptions[0]);

  const { filters } = useFilters();

  const filtersList = useMemo(() => {
    return filters ? (Object.values(filters) as Array<Filter>) : [];
  }, [filters]);

  const filterState = filtersList.find((filter) => filter.filterName === filterName);
  const filterExpression = filterState?.expression || '';
  const [paramValue, setParamValue] = useState<string>(filterExpression ?? 'any value');

  const handleChangeFilter = (value: string) => {
    const expressionType: ExpressionType = 'target';
    // Sanitize value
    const suffix = value.endsWith(' ') ? ' ' : '';
    value = sanitizeFilterExpression(value) + suffix;
    const filter: Filter = {
      filterName,
      expression: value,
      enabled: true,
      expressionType,
    };

    onChange?.(filter);
    setParamValue(value);
  };

  const handleRemoveFilter = () => {
    const expressionType: ExpressionType = 'target';
    const filter: Filter = {
      filterName,
      enabled: false,
      expression: paramValue,
      expressionType,
    };
    onChange?.(filter);
    if (removeFilter) {
      removeFilter();
    }
  };

  const handleParamChange = (value: string) => {
    setSelectedParam(value);
    if (onParamChange) onParamChange(value);
  };

  return (
    <Space alignContent={'center'} gap="u2" justifyItems={'flex-start'} width={'100%'}>
      <Box flex={1}>
        <StyledBISelect
          options={[
            {
              value: paramKey,
              label: toTitleCase(paramKey),
            },
            ...paramOptions.map((value) => ({
              value,
              label: toTitleCase(value),
            })),
          ]}
          listLayout={{ width: 'auto' }}
          value={selectedParam}
          onChange={handleParamChange}
          filterName={filterName}
        />
      </Box>
      <Box width={'fit-content'}>=</Box>
      <Space flex={1} gap="u2" align="center">
        <Box width={'100%'} flex={1}>
          <DsInput
            onValueChange={(e: DsInputCustomEvent<string>) => handleChangeFilter(e.detail)}
            type="text"
            value={paramValue}
            placeholder="Enter parameter value"
          />
        </Box>
        {removeFilter && (
          <BIIconButton
            icon="Delete"
            onClick={handleRemoveFilter}
            tooltip="Remove param"
            shape="round"
            aria-label="Remove param"
          />
        )}
      </Space>
    </Space>
  );
};
